import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

// Initialize Stripe - will be set from env variable
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

function CheckoutForm({ clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [shippingInfo, setShippingInfo] = useState({
    name: user?.displayName || '',
    email: user?.email || '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: ''
  });

  const handleShippingChange = (e) => {
    setShippingInfo({ ...shippingInfo, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      // Confirm payment with Stripe
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/order-confirmation`,
          payment_method_data: {
            billing_details: {
              name: shippingInfo.name,
              email: shippingInfo.email,
              phone: shippingInfo.phone,
              address: {
                line1: shippingInfo.address,
                city: shippingInfo.city,
                state: shippingInfo.state,
                postal_code: shippingInfo.zip,
                country: 'US'
              }
            }
          },
          shipping: {
            name: shippingInfo.name,
            phone: shippingInfo.phone,
            address: {
              line1: shippingInfo.address,
              city: shippingInfo.city,
              state: shippingInfo.state,
              postal_code: shippingInfo.zip,
              country: 'US'
            }
          }
        },
        redirect: 'if_required'
      });

      if (error) {
        setErrorMessage(error.message);
        setLoading(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment successful - clear cart and redirect
        clearCart();
        navigate(`/order-confirmation?payment_intent=${paymentIntent.id}`);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Shipping Information */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="font-serif text-xl font-bold text-ink mb-4">Shipping Information</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-ink-soft mb-1">Full Name *</label>
            <input
              type="text"
              name="name"
              value={shippingInfo.name}
              onChange={handleShippingChange}
              required
              className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-ink-soft mb-1">Email *</label>
            <input
              type="email"
              name="email"
              value={shippingInfo.email}
              onChange={handleShippingChange}
              required
              className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-ink-soft mb-1">Phone *</label>
            <input
              type="tel"
              name="phone"
              value={shippingInfo.phone}
              onChange={handleShippingChange}
              required
              className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-ink-soft mb-1">Address *</label>
            <input
              type="text"
              name="address"
              value={shippingInfo.address}
              onChange={handleShippingChange}
              required
              className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1">City *</label>
            <input
              type="text"
              name="city"
              value={shippingInfo.city}
              onChange={handleShippingChange}
              required
              className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1">State *</label>
            <input
              type="text"
              name="state"
              value={shippingInfo.state}
              onChange={handleShippingChange}
              required
              maxLength="2"
              className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-transparent"
              placeholder="CA"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1">ZIP Code *</label>
            <input
              type="text"
              name="zip"
              value={shippingInfo.zip}
              onChange={handleShippingChange}
              required
              className="w-full px-4 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Payment Information */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="font-serif text-xl font-bold text-ink mb-4">Payment Information</h2>
        <PaymentElement />
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {errorMessage}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full bg-sage-700 text-white px-6 py-4 rounded-lg font-bold text-lg hover:bg-sage-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg"
      >
        {loading ? 'Processing...' : 'Complete Purchase'}
      </button>
    </form>
  );
}

export default function Checkout() {
  const { cart, getCartTotal } = useCart();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Redirect if cart is empty
    if (cart.length === 0) {
      navigate('/shop');
      return;
    }

    // Create payment intent
    const createPaymentIntent = async () => {
      try {
        const token = user ? await user.getIdToken() : null;
        
        const response = await fetch(`${API_URL}/api/checkout/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({
            items: cart.map(item => ({
              productId: item.id,
              name: item.description || item.name,
              description: item.description,
              price: item.price,
              quantity: item.quantity,
              sku: item.sku,
              photoUrl: item.photoUrl
            }))
          })
        });

        if (!response.ok) {
          throw new Error('Failed to create payment intent');
        }

        const data = await response.json();
        setClientSecret(data.clientSecret);
        setLoading(false);
      } catch (err) {
        console.error('Error creating payment intent:', err);
        setError('Unable to initialize checkout. Please try again.');
        setLoading(false);
      }
    };

    createPaymentIntent();
  }, [cart, navigate, user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-700 mx-auto mb-4" />
          <p className="text-ink-soft">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="font-serif text-2xl font-bold text-ink mb-2">Checkout Error</h2>
          <p className="text-ink-soft mb-6">{error}</p>
          <button
            onClick={() => navigate('/shop')}
            className="bg-sage-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-sage-800 transition-colors"
          >
            Return to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-3xl font-bold text-ink mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            {clientSecret && (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <CheckoutForm clientSecret={clientSecret} />
              </Elements>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <h2 className="font-serif text-xl font-bold text-ink mb-4">Order Summary</h2>
              
              <div className="space-y-3 mb-4">
                {cart.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-ink-soft">
                      {item.description || item.name} × {item.quantity}
                    </span>
                    <span className="font-medium text-ink">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-ink-soft">Subtotal</span>
                  <span className="font-medium text-ink">${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-ink-soft">Tax</span>
                  <span className="font-medium text-ink">Calculated by Stripe</span>
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="text-lg font-bold text-ink">Total</span>
                  <span className="text-lg font-bold text-sage-700">
                    ${getCartTotal().toFixed(2)}+
                  </span>
                </div>
              </div>

              <p className="text-xs text-ink-muted mt-4">
                Final total including tax will be calculated at payment confirmation
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
